import { Injectable } from "@angular/core";
import { Transition } from "@uirouter/core";
import { ComplianceItemService } from "src/app/services/compliance-item.service";
import { firstValueFrom } from "rxjs";
import { UserCycleService } from "src/app/services/user-cycle/user-cycle.service";
import { VerifyService } from "src/app/services/verify.service";
import { FileService } from "src/app/services/file.service";
import { FileAttachmentType } from "src/app/supervision-management/compliance-items/compliance-item/enums/file-attachment-type.enum";
import { ItemPacketService } from "src/app/services/item-packet/item-packet.service";
import { SessionService, UserService } from "@edl-development/edl-common";
import { PortalHelpersService } from "src/app/services/portal-helpers/portal-helpers.service";
import { RequestItemService } from "src/app/services/request-item.service";
import { PagingHelpersService } from "src/app/services/paging-helpers/paging-helpers.service";
import { TagService } from "src/app/services/tag.service";
import { UserRequestService } from "src/app/services/user-request.service";
import { RequestTypeFilter } from "../enums/request-type-filter.enum";
import { AupRequestItemStatus } from "../enums/aup-request-item-status.enum";


export function CheckCompliancePermissions(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);
    var portalHelpersService: PortalHelpersService = trans.injector().get(PortalHelpersService);
    const portalSettings = sessionService.PortalSettings;

    if (!portalSettings || !portalHelpersService.canViewComplianceItems(portalSettings)) {
        return trans.router.stateService.target('public.unauthorized');
    }
}

export function CheckRequestPermissions(trans: Transition) {
    var sessionService: SessionService = trans.injector().get(SessionService);
    var portalHelpersService: PortalHelpersService = trans.injector().get(PortalHelpersService);
    const portalSettings = sessionService.PortalSettings;

    if (!portalSettings || !portalHelpersService.canViewRequestItems(portalSettings)) {
        return trans.router.stateService.target('public.unauthorized');
    }
}

export function resolveCurrentPage(transition: Transition) {
  const params = transition.params();
  return +(params.page || 1);
}

@Injectable()
export class AllUsersProgressResolver {
    constructor(
        private sessionService: SessionService,
        private pagingHelpersService: PagingHelpersService,
        private complianceItemService: ComplianceItemService,
        private userCycleService: UserCycleService,
        private verifyService: VerifyService,
        private fileService: FileService,
        private itemPacketService: ItemPacketService,
        private requestItemService: RequestItemService,
        private userRequestService: UserRequestService,
        private userService: UserService,
        private portalHelpersService: PortalHelpersService,
        private tagService: TagService
    ) {}
    
    resolveAccountByUserId = (userId: string) => {
        if (userId == this.sessionService.user.UserID) {
            return Promise.resolve(this.sessionService.user);
        }

        return firstValueFrom(this.userService.getAccountByUserId(userId));
    }

    resolveCplUserStatusSummary = (userId: string) => {
        
        if(!this.portalHelpersService.canViewComplianceItems(this.sessionService.PortalSettings)) {
            return Promise.resolve(undefined);
        }

        return firstValueFrom(this.complianceItemService.getUserStatusSummary(userId));
    }

    resolveUserSummary = (userId: string, transition: Transition) => {
        const { archived, itemPackets } = transition.params();
        const paging = this.pagingHelpersService.generatePagingModel(transition);
        paging.searchBy = archived;

        const itemPacketsArray = this.pagingHelpersService.toArrayParam(itemPackets);
        return firstValueFrom(
            this.complianceItemService.getUserSummary(userId, paging, itemPacketsArray)
        );
    }

    resolveUserCycleDetails = (transition: Transition) => {
        let params = transition.params();

        return firstValueFrom(this.userCycleService.getUserCycleDetails(params.cycleId));
    }
    
    resolveUserCycleHistoryByCycle = (transition: Transition) => {
        let params = transition.params();

        return firstValueFrom(this.complianceItemService.getUserCycleHistoryByCycle(params.cycleId, 0, 40, null, null));
    }
    
    resolveActionRequired = (transition: Transition) => {
        let params = transition.params();

        return (params.actionRequired == 'true') ;
    }
    
    resolveUserCycleVerificationHistory = (transition: Transition) => {
        const params = transition.params();

        return firstValueFrom(this.verifyService.getVerifyHistoryForEndUser(params.cycleId));
    }
    
    resolveUserCycleEvidence = (transition: Transition) => {
        const params = transition.params();

        return firstValueFrom(this.fileService.getFilesForItem(FileAttachmentType.COMPLIANCECYCLE, params.cycleId));
    }
    
    resolveItemPacketsForUser(userId: string) {
        return !!userId ? firstValueFrom(this.itemPacketService.getPacketsForUser(userId)) : Promise.resolve([]);
    }

    resolveSelectedRequest(transition: Transition) {
        const customData = transition.options().custom;
        if (customData && customData.selectedRequest) {
            return Promise.resolve(customData.selectedRequest);
        }

        const requestId = transition.params().requestId;
        return firstValueFrom(this.requestItemService.getRequestItem(requestId, false));
    }

    resolveTags() {
        return firstValueFrom(this.tagService.getTags(this.sessionService.user.UserPortal.PortalID, ''));
    }

    resolveRequestsForUser(userId: string, isMyItems: boolean, transition: Transition) {
        const params = transition.params();

        // Load saved filters from SessionService
        const storageKey = `aup_requests_tab_${userId}`;
        const savedFilters = this.sessionService.GetSessionValue(storageKey) as any;

        // Priority: URL params ?? saved filters ?? defaults
        const page = params.page || 1;
        const searchBy = params.searchBy ?? savedFilters?.searchBy ?? '';
        const status = params.status ?? savedFilters?.status ?? AupRequestItemStatus.Active;
        const tag = params.tag ?? savedFilters?.tag ?? 0;
        const requestType: RequestTypeFilter = params.requestType ?? savedFilters?.requestType ?? RequestTypeFilter.MyRequests;
        const orderBy = params.orderBy;
        const ascending = params.ascending === 'true' ? true : false;
        const offset = (page - 1) * 20;
        const limit = 20;

        if (isMyItems) {
            return firstValueFrom(this.userRequestService
                .getMyRequests(
                    offset,
                    limit,
                    orderBy,
                    ascending,
                    searchBy,
                    status,
                    tag,
                    true, // updateStatusCounts
                    requestType,
                    true  // includeUserStatusCount
                ));
        }

        return firstValueFrom(this.userRequestService
            .getRequestsForUser(
                userId,
                offset,
                limit,
                orderBy,
                ascending,
                searchBy,
                status,
                tag,
                requestType,
                false, // updateStatusCounts
                true // includeUserStatusCount
            )
        );
    }
}